/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import {
  DATE_MONTH_YEAR_FORMAT,
  GB_LOCALE_PATTERN_HYPHEN,
  US_LOCALE,
  JP_LOCALE,
  FR_LOCALE,
  IT_LOCALE,
  ES_LOCALE,
  PT_BR_LOCALE,
  YEAR_MON_DATE_FORMAT,
  KR_LOCALE,
  YEAR_MON_DATE_DOTTED_FORMAT,
  RU_LOCALE,
  DATE_DOTTED_FORMAT,
  DE_LOCALE,
  MOMENT_CN_LANG,
  TW_LOCALE,
  DATE_FORMAT_YEAR_MONTH_DAY,
  DATE_FORMAT_VALUE,
  ADULT_ONLY,
  DEVICE_TYPE_MOBILE,
  DEVICE_TYPE_DESKTOP,
  AR_LOCALE,
} from '../constants/Calendar.constant';
import { changeCountryCodeForChina, convertArabicToEnglishNumbers } from './helpers';
import { LOCALE_ZH_CN } from '@marriott/mi-ui-library';

// function to filter query paramteres with empty, null, or undefined value
const filterParams = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, val]) => !['', undefined, null, 'Invalid date'].includes(val as any) && key !== ''
    )
  );
};

const roomCountMap: Record<string, string> = {
  '4-9': '4',
  '10-25': '10',
  '26+': '26',
};

const roomCountMapForJPLocale: Record<string, string> = {
  '4〜9': '4',
  '10〜25': '10',
  '26以上': '26',
};

let SUBDIRECTORY_PREFIX = '';

export function setSubDirectoryPrefix(prefix = '') {
  SUBDIRECTORY_PREFIX = prefix;
}

/**
 *
 * @param url
 * This is used for prefix the url. This will check for subdirectory prefix
 * if present then it will be prefixed otherwise the url is returned without prefix.
 */
export const addSubDirectoryPrefix = function (url = '') {
  if (
    SUBDIRECTORY_PREFIX &&
    url?.startsWith('/') &&
    url?.substring(1, findNthOccurence(url, 1, '/'))?.toLowerCase() !== SUBDIRECTORY_PREFIX?.toLowerCase()
  ) {
    return SUBDIRECTORY_PREFIX ? '/' + SUBDIRECTORY_PREFIX + url : url;
  } else return url;
};

/**
 *
 * @param url as str
 * This is used find the nth occurence of a char in string
 */
const findNthOccurence = function (str: string, nth: number, char: string) {
  let index = 0;
  for (let i = 0; i < nth; i += 1) {
    if (index !== -1) index = str.indexOf(char, index + 1);
  }
  return index;
};

//function for generating params for Hotels and HP search form submit
export const performSubmitSearchFormAction = (
  submitSearchParams: any,
  searchformRef: any,
  submitSearchUrl: string | undefined,
  availabilitySearchUrl: string | undefined,
  currentLocale: string | undefined,
  sessionData: any,
  isAvailabilityScenario?: boolean
) => {
  // Logic to pass the expected value for roomCount query parameter
  const params = filterParams(submitSearchParams);
  if (params?.['roomCount']) {
    if (currentLocale === JP_LOCALE) {
      params['roomCount'] = roomCountMapForJPLocale[params['roomCount'] as string] || params['roomCount'];
    } else {
      params['roomCount'] = roomCountMap[params['roomCount'] as string] || params['roomCount'];
    }
  }
  const adultErrorKey =
    sessionData?.sessionData?.cacheData?.data?.AriesReservation?.errorMessages?.availabilitySearchFormErrorMessages?.errorMessageKeys?.includes(
      ADULT_ONLY
    );

  const roomCount = Number(
    roomCountMap[params['numberOfRooms'] as string] || roomCountMapForJPLocale[params['numberOfRooms'] as string]
  );

  //Adult only scenario - User will be navigated to search page with other available properties with child accepted
  if (isAvailabilityScenario && adultErrorKey && params?.['childrenCount'] !== 0) {
    searchformRef.current.action = addSubDirectoryPrefix(submitSearchUrl);
  } else if (isAvailabilityScenario && roomCount >= 10) {
    params['roomCount'] = roomCount.toString();
    searchformRef.current.action = addSubDirectoryPrefix(submitSearchUrl);
  } else if (params?.['propertyCode'] || (isAvailabilityScenario && adultErrorKey && params?.['childrenCount'] == 0)) {
    searchformRef.current.action = addSubDirectoryPrefix(availabilitySearchUrl);
  } else {
    searchformRef.current.action = addSubDirectoryPrefix(submitSearchUrl);
  }
  for (const param in params) {
    const input = document.createElement('input');
    input.setAttribute('name', param);
    input.setAttribute('value', params[param] as string);
    input.setAttribute('type', 'hidden');
    searchformRef.current.appendChild(input);
  }
  searchformRef.current.submit();
};

/**
 * Function to get formatted date by locale
 * @param {string}  currentLocale pass the current locale
 * @param {any}  dates pass the current locale
 * @param {string}  DATE_FORMAT_VALUE pass the current locale
 */
export const getFormattedDateByLocale = (currentLocale: string, dates: any, DATE_FORMAT_VALUE: string) => {
  let fromDateValue = moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE) ?? '';
  let toDateValue = moment(dates?.toDate)?.format(DATE_FORMAT_VALUE) ?? '';
  switch (currentLocale?.replace('_', '-')) {
    case GB_LOCALE_PATTERN_HYPHEN:
    case FR_LOCALE:
    case IT_LOCALE:
    case ES_LOCALE:
    case PT_BR_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_MONTH_YEAR_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_MONTH_YEAR_FORMAT);
      }
      break;
    case JP_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(YEAR_MON_DATE_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(YEAR_MON_DATE_FORMAT);
      }
      break;
    case KR_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(YEAR_MON_DATE_DOTTED_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(YEAR_MON_DATE_DOTTED_FORMAT);
      }
      break;
    case RU_LOCALE:
    case DE_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_DOTTED_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_DOTTED_FORMAT);
      }
      break;
    case LOCALE_ZH_CN:
    case TW_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_FORMAT_YEAR_MONTH_DAY);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_FORMAT_YEAR_MONTH_DAY);
      }
      break;
    case AR_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = convertArabicToEnglishNumbers(moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE));
      }
      if (dates?.toDate) {
        toDateValue = convertArabicToEnglishNumbers(moment(dates?.toDate)?.format(DATE_FORMAT_VALUE));
      }
      break;
    default:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_FORMAT_VALUE);
      }
      break;
  }

  return { fromDateValue, toDateValue };
};

//function for generating params for Hotels and HP search form submit
export const formSubmitSearchRequestParams = (
  destination: any,
  destinationHiddenFields: any,
  roomsAndGuests: any,
  dates: any,
  usePoints: any,
  marriottBrands: any,
  specialRates: any,
  destinationFieldDetails: any,
  resortsHiddenFields: any,
  searchformRef: any,
  submitSearchUrl: string | undefined,
  availabilitySearchUrl: string | undefined,
  isAdvanceSearch: string | undefined,
  currentLocale?: string,
  localDateFormat?: string,
  sessionData?: any,
  isAvailabilityScenario?: boolean,
  addBrandFilters = false,
  maxSearchRadius?: any
): void => {
  const DATE_FORMAT_VALUE = localDateFormat ?? 'MM/DD/YYYY';
  const secondaryText = destinationHiddenFields?.secondaryDescription
    ? destinationHiddenFields?.secondaryDescription
    : destinationHiddenFields?.state && destinationHiddenFields?.country
    ? `${destinationHiddenFields?.state}, ${changeCountryCodeForChina(destinationHiddenFields?.country)}`
    : '';
  const mainText = destinationHiddenFields?.primaryDescription ?? destinationHiddenFields?.city ?? '';
  const fromDateValue = getFormattedDateByLocale(currentLocale ?? US_LOCALE, dates, DATE_FORMAT_VALUE)?.fromDateValue;
  const toDateValue = getFormattedDateByLocale(currentLocale ?? US_LOCALE, dates, DATE_FORMAT_VALUE)?.toDateValue;
  const numberOfNights = Math.ceil(moment(dates?.toDate)?.diff(dates?.fromDate, 'days', true) ?? 0);
  const lastDefaultDate = dates?.lastDefaultDate
    ? currentLocale?.replace('_', '-') === AR_LOCALE
      ? convertArabicToEnglishNumbers(moment(dates?.lastDefaultDate)?.format(DATE_FORMAT_VALUE))
      : moment(dates?.lastDefaultDate)?.format(DATE_FORMAT_VALUE)
    : '';

  const childrenAgesString =
    typeof roomsAndGuests?.childrenAges == 'object'
      ? roomsAndGuests?.childrenAges
          ?.map((age: string) => (isNaN(parseInt(age, 10)) && age !== '<1' ? '' : age === '<1' ? 0 : age))
          .toString()
      : roomsAndGuests?.childrenAges
          ?.split(',')
          ?.map((age: string) => (isNaN(parseInt(age, 10)) && age !== '<1' ? '' : age === '<1' ? 0 : age))
          .toString();

  const hotelTypeValue = resortsHiddenFields?.hotelType?.toString() ?? '';
  const activitiesValue = resortsHiddenFields?.activities?.toString() ?? '';
  const amenitiesValue = resortsHiddenFields?.amenities?.toString() ?? '';
  const screenWidth = window.innerWidth;

  let paramsObject = {};
  const roomCountInURL =
    roomCountMap[roomsAndGuests?.numRooms as string] ??
    roomCountMapForJPLocale[roomsAndGuests?.numRooms as string] ??
    roomsAndGuests?.numRooms;
  const commonParamsObject = {
    propertyCode: '',
    fromToDate: lastDefaultDate,
    fromToDate_submit: toDateValue,
    fromDate: fromDateValue,
    toDate: toDateValue,
    toDateDefaultFormat: toDateValue,
    fromDateDefaultFormat: fromDateValue,
    flexibleDateSearch: dates?.flexible ? 'true' : 'false',
    't-start': fromDateValue,
    't-end': toDateValue,
    lengthOfStay: numberOfNights,
    childrenCountBox: `${roomsAndGuests?.numChildrenPerRoom} Children Per Room`,
    childrenCount: roomsAndGuests?.numChildrenPerRoom,
    childrenAges: childrenAgesString,
    clusterCode: specialRates?.specialRateCode,
    corporateCode: specialRates?.corporateCode,
    useRewardsPoints: usePoints?.rewardsRedemption,
    marriottBrands: addBrandFilters ? marriottBrands : '',
    hotelType: hotelTypeValue,
    activities: activitiesValue,
    amenities: amenitiesValue,
    isAdvanceSearch: isAdvanceSearch,
  };
  if (destinationFieldDetails?.recentlyViewedPopertyCode || destinationHiddenFields?.recentlyViewedPopertyCode) {
    const availabilitySearchParamsObject = {
      propertyCode:
        destinationFieldDetails?.recentlyViewedPopertyCode?.length > 0
          ? destinationFieldDetails?.recentlyViewedPopertyCode
          : destinationHiddenFields?.recentlyViewedPopertyCode,
      roomCountBox: `${roomsAndGuests?.numRooms} Rooms`,
      numberOfRooms: typeof roomsAndGuests?.numRooms === 'string' ? roomCountInURL : roomsAndGuests?.numRooms,
      guestCountBox: `${roomsAndGuests?.numAdultsPerRoom} Adults Per Room`,
      numberOfAdults: roomsAndGuests?.numAdultsPerRoom,
    };

    const roomCount = Number(typeof roomsAndGuests?.numRooms === 'string' ? roomCountInURL : roomsAndGuests?.numRooms);

    let submitSearchParams = {};

    if (isAvailabilityScenario && roomCount >= 10) {
      submitSearchParams = {
        'destinationAddress.types': destinationHiddenFields?.types,
        'destinationAddress.type': destinationHiddenFields?.destinationType,
        'destinationAddress.latitude': destinationHiddenFields?.latitude,
        isInternalSearch: true,
        'destinationAddress.stateProvinceDisplayName': destinationHiddenFields?.state,
        countryName: changeCountryCodeForChina(destinationHiddenFields?.country),
        'destinationAddress.stateProvince': destinationHiddenFields?.state,
        searchRadius: maxSearchRadius || destinationHiddenFields?.searchRadius,
        singleSearchAutoSuggest: 'Unmatched',
        'destinationAddress.placeId': destination?.destinationAddressPlaceId,
        'destinationAddress.country': changeCountryCodeForChina(destinationHiddenFields?.country),
        'destinationAddress.address': destinationHiddenFields?.address,
        'destinationAddress.secondaryText': secondaryText,
        'destinationAddress.postalCode': destinationHiddenFields?.postalAddress,
        'destinationAddress.city': destinationHiddenFields?.city,
        'destinationAddress.mainText': mainText,
        'destinationAddress.longitude': destinationHiddenFields?.longitude,
        initialRequest: false,
        'destinationAddress.website': destinationHiddenFields?.website,
        'destinationAddress.destination':
          sessionData?.sessionData?.cacheData?.data?.AriesSearch?.searchCriteria?.address?.destination ||
          destination?.displayText,
      };
    }
    paramsObject = { ...commonParamsObject, ...availabilitySearchParamsObject, ...submitSearchParams };
  } else {
    const submitSearchParamsObject = {
      roomTypeCode: '',
      recordsPerPage: 20,
      autoSuggestItemType: '',
      'destinationAddress.types': destinationHiddenFields?.types,
      'destinationAddress.type': destinationHiddenFields?.destinationType,
      'destinationAddress.latitude': destinationHiddenFields?.latitude,
      propertyCode: '',
      'destinationAddress.stateProvinceShort': '',
      isInternalSearch: true,
      'destinationAddress.cityPopulation': '',
      vsInitialRequest: false,
      searchType: 'InCity',
      'destinationAddress.locality': '',
      showAddressPin: '',
      'destinationAddress.stateProvinceDisplayName': destinationHiddenFields?.state,
      'destinationAddress.destinationPageDestinationAddress': '',
      countryName: changeCountryCodeForChina(destinationHiddenFields?.country),
      'destinationAddress.stateProvince': destinationHiddenFields?.state,
      searchRadius: maxSearchRadius || destinationHiddenFields?.searchRadius,
      singleSearchAutoSuggest: 'Unmatched',
      'destinationAddress.placeId': destination?.destinationAddressPlaceId,
      airportName: '',
      'for-hotels-nearme': 'Near',
      suggestionsPropertyCode: '',
      'destinationAddress.country': changeCountryCodeForChina(destinationHiddenFields?.country),
      'destinationAddress.name': '',
      poiCity: '',
      'destinationAddress.countryShort': '',
      poiName: '',
      'destinationAddress.address': destinationHiddenFields?.address,
      'search-countryRegion': '',
      collapseAccordian: 'is-hidden',
      singleSearch: true,
      'destinationAddress.cityPopulationDensity': '',
      'destinationAddress.secondaryText': secondaryText,
      'destinationAddress.postalCode': destinationHiddenFields?.postalAddress,
      'destinationAddress.city': destinationHiddenFields?.city,
      'destinationAddress.mainText': mainText,
      airportCode: '',
      isTransient: true,
      'destinationAddress.longitude': destinationHiddenFields?.longitude,
      initialRequest: false,
      'destinationAddress.website': destinationHiddenFields?.website,
      'search-locality': '',
      flexibleDateSearchRateDisplay: false,
      propertyName: '',
      isSearch: true,
      marriottRewardsNumber: '',
      isRateCalendar: dates?.flexible ? 'true' : 'false',
      incentiveType_Number: '',
      incentiveType: '',
      flexibleDateLowestRateMonth: '',
      flexibleDateLowestRateDate: '',
      isMultiRateSearch: '',
      multiRateMaxCount: '',
      multiRateCorpCodes: '',
      useMultiRateRewardsPoints: '',
      multiRateClusterCodes: '',
      multiRateCorpCodesEntered: '',
      lowestRegularRate: '',
      'js-location-nearme-values': '',
      'destinationAddress.destination': destination?.displayText,
      isHideFlexibleDateCalendar: false,
      roomCountBox: `${roomsAndGuests?.numRooms} Room`,
      roomCount: typeof roomsAndGuests?.numRooms === 'string' ? roomCountInURL : roomsAndGuests?.numRooms,
      guestCountBox: `${roomsAndGuests?.numAdultsPerRoom} Adult Per Room`,
      numAdultsPerRoom: roomsAndGuests?.numAdultsPerRoom,
      deviceType: screenWidth < 992 ? DEVICE_TYPE_MOBILE : DEVICE_TYPE_DESKTOP,
    };
    paramsObject = { ...commonParamsObject, ...submitSearchParamsObject };
  }
  performSubmitSearchFormAction(
    paramsObject,
    searchformRef,
    submitSearchUrl,
    availabilitySearchUrl,
    currentLocale,
    sessionData,
    isAvailabilityScenario
  );
};
